import { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { useJobs } from './hooks';
import { ActiveTab } from './ActiveTab';
import { CompletedTab } from './CompletedTab';
import { TemplatesTab } from './TemplatesTab';
import { CreateJobPanel } from './CreateJobPanel';
import { JobDetailPanel } from './JobDetailPanel';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import { JOB_TABS, type JobTab } from './config';
import type { JobRow, JobFilters } from './hooks';
import { usePagination } from '../../hooks/usePagination';
import { useSortState } from '../../hooks/useSortState';

export function JobsPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const [tab, setTab] = useState<JobTab>('active');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<JobRow | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const pagination = usePagination('jobs');
  const sort = useSortState('created_at', 'desc');

  const { jobs, completedJobs, totalCount, users, loading, error, summary, filters, setFilters, createJob, createJobBatch, updateStatus } = useJobs(business?.id, {
    from: pagination.from,
    to: pagination.to,
    sortColumn: sort.column,
    sortDirection: sort.direction,
  });
  const { features, isLensActive, industryName, categoryId: lensCategoryId } = useIndustryLens();

  useEffect(() => { document.title = 'CrewBit · Jobs'; }, []);

  const handleAdvance = async (id: string, status: string) => {
    if (!user) return;
    const ok = await updateStatus(id, status, user.id);
    if (ok) setToast({ msg: `Job marked ${status}`, type: 'success' });
  };

  const handleFilterChange = useCallback((newFilters: JobFilters) => {
    setFilters(newFilters);
    pagination.setPage(1);
  }, [setFilters, pagination]);

  if (isLensActive && !features.showJobs) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Jobs</div>
        <div style={{ padding: '60px 20px', textAlign: 'center', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: palette.g700, marginBottom: 6 }}>Jobs is not typically used by {industryName} businesses</div>
          <div style={{ fontSize: 13.5, color: palette.g500 }}>Switch your industry lens to see this module, or exit the lens to return to your view.</div>
        </div>
      </div>
    );
  }

  if (loading && totalCount === null) {
    return (
      <div>
        <Skeleton width={100} height={24} style={{ marginBottom: 24 }} />
        <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><TableSkeleton rows={5} columns={6} /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Jobs</div>
        <div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Jobs</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Work orders and job management</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
          border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600,
          color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
        }}><Plus size={15} /> New Job</button>
      </div>

      <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2, marginBottom: 16, width: 'fit-content' }}>
        {JOB_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '6px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
            background: tab === t.key ? palette.g800 : 'transparent',
            color: tab === t.key ? palette.white : palette.g500,
            transition: `all ${transition.fast}`,
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card }}>
        {tab === 'active' && (
          <ActiveTab jobs={jobs} totalCount={totalCount} users={users} summary={summary}
            filters={filters} setFilters={handleFilterChange}
            sort={sort} pagination={pagination} loading={loading}
            onSelect={setSelected} onAdvance={handleAdvance} onNewJob={() => setShowCreate(true)} />
        )}
        {tab === 'completed' && <CompletedTab jobs={completedJobs} onSelect={setSelected} />}
        {tab === 'templates' && <TemplatesTab businessId={business?.id ?? ''} industryId={lensCategoryId ?? business?.industry ?? ''} userId={user?.id ?? ''} />}
      </div>

      <CreateJobPanel isOpen={showCreate} onClose={() => setShowCreate(false)}
        onCreate={async (data, uid) => { const id = await createJob(data as Record<string, unknown>, uid); if (id) setToast({ msg: 'Job created', type: 'success' }); return id; }}
        onCreateBatch={async (data, dates, uid) => { const ok = await createJobBatch(data as Record<string, unknown>, dates, uid); if (ok) setToast({ msg: `Created ${dates.length} jobs`, type: 'success' }); }}
        users={users} userId={user?.id ?? ''} />
      <JobDetailPanel job={selected} businessId={business?.id ?? ''} userId={user?.id ?? ''}
        onClose={() => setSelected(null)} onAdvance={handleAdvance} />
      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
